import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, AlertTriangle, RotateCw, ChevronRight } from "lucide-react";
import { api } from "../lib/api";
import { StatusBadge, SimBadge } from "../components/Badges";
import { ClubCrest } from "../components/ClubCrest";
import { CountryFilterRow } from "../components/CountryFilterRow";
import { Flag } from "../components/Flag";

const CONTAINER = "mx-auto max-w-[1700px] px-4 sm:px-8 lg:px-12";

function SkeletonCard() {
  return (
    <div className="rounded-xl bg-[#0f0f0f] border border-[rgba(212,175,55,0.14)] p-5 animate-pulse">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-lg bg-[#1a1407]" />
        <div className="flex-1 space-y-2"><div className="h-4 w-2/3 bg-[#1a1407] rounded" /><div className="h-3 w-1/3 bg-[#141414] rounded" /></div>
      </div>
      <div className="h-2 w-full bg-[#141414] rounded mt-5" />
    </div>
  );
}

function RosterBar({ roster }) {
  const pct = Math.min(100, Math.round((roster.count / roster.max) * 100));
  const good = roster.eligible;
  return (
    <div>
      <div className="flex items-center justify-between text-[11px] font-mono mb-1">
        <span className="text-slate-500 uppercase tracking-wider">Roster</span>
        <span className="text-slate-300 tabular-nums">{roster.count}<span className="text-slate-600">/{roster.max}</span></span>
      </div>
      <div className="h-1.5 rounded-full bg-[#1a1407] overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: good ? "linear-gradient(90deg,#9B781E,#F3D279)" : "#b45309" }} />
      </div>
      <div className={`mt-1.5 text-[10px] font-mono uppercase tracking-wider ${good ? "text-emerald-400/90" : "text-amber-400/90"}`}>
        {roster.label}
      </div>
    </div>
  );
}

function ClubCard({ c }) {
  // Full club name always visible — responsive sizing, wraps up to 2 lines, never truncated.
  const nameLen = (c.name || "").length;
  const nameSize = nameLen > 30 ? "text-sm" : nameLen > 20 ? "text-base" : "text-lg";
  return (
    <Link
      to={`/club/${c.id}`}
      data-testid={`club-card-${c.wsbf_club_id}`}
      className="group relative h-full flex flex-col rounded-xl bg-[#0f0f0f] border border-[rgba(212,175,55,0.16)] p-5 hover:border-[#E5B842]/55"
      style={{ transition: "border-color 0.18s ease, transform 0.18s ease, box-shadow 0.18s ease" }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 10px 30px -12px rgba(212,175,55,0.35)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = ""; }}
    >
      <div className="flex items-start gap-3">
        <ClubCrest club={c} size={52} />
        <div className="min-w-0 flex-1">
          <div className={`font-heading ${nameSize} font-bold uppercase tracking-tight text-slate-50 leading-snug break-words`}>
            <Flag country={c.country} width={20} className="mr-1.5 align-middle" testid={`club-flag-${c.wsbf_club_id}`} />
            <span>{c.name}</span>
            {c.is_simulated && <span className="ml-1.5 inline-flex align-middle"><SimBadge /></span>}
          </div>
          <div className="text-xs text-slate-400 mt-1">{c.city ? `${c.city}, ${c.country?.name}` : c.country?.name}</div>
        </div>
        <StatusBadge status={c.status} />
      </div>
      <div className="mt-4"><RosterBar roster={c.roster} /></div>
      <div className="flex items-center justify-between mt-auto pt-4">
        <span className="font-mono text-[11px] text-[#E5B842] tracking-widest">{c.wsbf_club_id}</span>
        <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-[#E5B842]" style={{ transition: "color 0.18s ease" }} />
      </div>
    </Link>
  );
}

export default function ClubsList() {
  const [clubs, setClubs] = useState(null);
  const [error, setError] = useState(false);
  const [q, setQ] = useState("");
  const [countries, setCountries] = useState([]);
  const [totalAll, setTotalAll] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();

  // Country facets — derived dynamically from the DB (new countries appear automatically).
  useEffect(() => {
    api.get("/clubs/countries").then(({ data }) => {
      setCountries(data.countries || []);
      setTotalAll(data.total || 0);
    }).catch(() => { setCountries([]); setTotalAll(0); });
  }, []);

  // Selected country = URL param (shareable); default Greece, falling back to ALL
  // if Greece has no clubs. Resolve once facets have loaded.
  const paramCountry = searchParams.get("country");
  const hasGreece = countries.some((c) => c.code === "GRE");
  const selected = paramCountry
    ? paramCountry.toUpperCase()
    : (countries.length === 0 ? "GRE" : hasGreece ? "GRE" : "ALL");

  const selectCountry = (code) => {
    const next = new URLSearchParams(searchParams);
    next.set("country", code);
    setSearchParams(next, { replace: true });
  };

  const load = useCallback((term, country) => {
    setClubs(null); setError(false);
    const parts = [`limit=100`];
    if (term) parts.push(`search=${encodeURIComponent(term)}`);
    if (country && country !== "ALL") parts.push(`country=${encodeURIComponent(country)}`);
    api.get(`/clubs?${parts.join("&")}`).then(({ data }) => setClubs(data.clubs)).catch(() => setError(true));
  }, []);

  // Server-side search + country filter (the /clubs list is capped at 100, so
  // client-side filtering could never reach every club in the registry).
  useEffect(() => {
    const t = setTimeout(() => load(q.trim(), selected), q ? 300 : 0);
    return () => clearTimeout(t);
  }, [q, selected, load]);

  const filtered = clubs || [];
  const cappedNoSearch = clubs && !q && clubs.length >= 100;
  const selectedName = countries.find((c) => c.code === selected)?.name;

  return (
    <div className="animate-fade-up pb-16">
      <section className="relative overflow-hidden border-b border-[rgba(212,175,55,0.22)] bg-[#000000]">
        <div className="absolute inset-0 mesh-grid opacity-[0.18] pointer-events-none" />
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(120% 140% at 85% 20%, rgba(212,175,55,0.14) 0%, transparent 58%)" }} />
        <div className={`${CONTAINER} relative py-12 sm:py-14`}>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#E5B842] font-mono font-bold mb-3">Sanctioned Clubs</div>
          <h1 className="font-heading text-5xl sm:text-6xl font-black uppercase tracking-tight text-slate-50 leading-none wsbf-slant-block">Clubs &amp; Rosters</h1>
          <p className="text-slate-400 mt-3 text-sm sm:text-base max-w-2xl">Federation-registered clubs with competition-eligible rosters.</p>
        </div>
      </section>

      <div className={`${CONTAINER} pt-8`}>
        <div className="relative max-w-3xl">
          <Search className="w-5 h-5 text-[#E5B842]/70 absolute left-4 top-1/2 -translate-y-1/2" />
          <input
            data-testid="clubs-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by club name or WSTF Club ID"
            className="w-full bg-[#0a0a0a] border border-[rgba(212,175,55,0.22)] rounded-xl pl-12 pr-4 py-4 text-slate-100 text-base placeholder:text-slate-600 focus:outline-none focus:border-[#E5B842]/70 focus:ring-2 focus:ring-[#E5B842]/25"
            style={{ transition: "border-color 0.15s ease, box-shadow 0.15s ease" }}
          />
        </div>

        {countries.length > 0 && (
          <CountryFilterRow countries={countries} selected={selected} totalAll={totalAll} onSelect={selectCountry} />
        )}

        {cappedNoSearch && (
          <div className="mt-3 text-xs font-mono text-slate-500" data-testid="clubs-capped-note">
            Showing the first 100 sanctioned clubs — use search to find any club in the full registry.
          </div>
        )}
      </div>

      <div className={`${CONTAINER} pt-8`}>
        {error ? (
          <div data-testid="clubs-error" className="text-center py-20 border border-dashed border-rose-500/30 rounded-xl">
            <AlertTriangle className="w-7 h-7 text-rose-400 mx-auto mb-3" />
            <div className="text-slate-300 font-mono text-sm uppercase tracking-widest mb-4">Could not load clubs</div>
            <button onClick={() => load(q.trim(), selected)} data-testid="clubs-retry" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold uppercase tracking-wide bg-[#161616] border border-[rgba(212,175,55,0.3)] text-slate-200 hover:border-[#E5B842]/60 hover:text-[#E5B842]" style={{ transition: "border-color 0.15s ease, color 0.15s ease" }}>
              <RotateCw className="w-4 h-4" /> Retry
            </button>
          </div>
        ) : !clubs ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="clubs-skeleton">
            {Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} />)}
          </div>
        ) : filtered.length === 0 ? (
          <div data-testid="clubs-empty" className="text-center py-20 text-slate-500 font-mono text-sm uppercase tracking-widest border border-dashed border-[rgba(212,175,55,0.25)] rounded-xl">
            {q ? "No clubs found" : selected !== "ALL" && selectedName ? `No clubs in ${selectedName} yet.` : "No clubs found"}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="clubs-grid">
            {filtered.map((c) => <ClubCard key={c.id} c={c} />)}
          </div>
        )}
      </div>
    </div>
  );
}
